import type { ColumnFiltersState, ColumnVisibilityState } from "@tanstack/react-table";
import { Columns3, Filter, Plus, RotateCw, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
} from "~/components/ui";
import type { CategoryRow } from "~/features/admin/categories/types";
import { useIsActiveRolloAdmin } from "~/features/authz/authorization";
import { cn } from "~/lib/utils";
import { CategoriesTable } from "./CategoriesTable";
import { CategoryFormDialog } from "./CategoryFormDialog";

type TypeFilter = "entity" | "relation" | null;
const ALL = "__all";

const CATEGORY_COLUMNS = [
  { id: "typeId", labelKey: "model.category.type" },
  { id: "displayName", labelKey: "model.common.name" },
  { id: "localeLabel", labelKey: "ui.label" },
  { id: "relationTypeLabel", labelKey: "model.relationType.modelName" },
] as const;

interface CategoriesListViewProps {
  rows: CategoryRow[];
  onSelect: (id: string) => void;
  search: string;
  onSearchChange: (next: string) => void;
  typeId: TypeFilter;
  onTypeIdChange: (next: TypeFilter) => void;
  excludeDeactivated: boolean;
  onExcludeDeactivatedChange: (next: boolean) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  onRefresh: () => void;
}

export function CategoriesListView({
  rows,
  onSelect,
  search,
  onSearchChange,
  typeId,
  onTypeIdChange,
  excludeDeactivated,
  onExcludeDeactivatedChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  onRefresh,
}: CategoriesListViewProps) {
  const { t } = useTranslation();
  const isActiveRolloAdmin = useIsActiveRolloAdmin();
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>({});
  const [createOpen, setCreateOpen] = useState(false);

  const filteredRows = useMemo(
    () => (excludeDeactivated ? rows.filter((row) => !row.isDeactivated) : rows),
    [rows, excludeDeactivated],
  );

  const columnFilters: ColumnFiltersState = useMemo(() => (typeId ? [{ id: "typeId", value: typeId }] : []), [typeId]);

  const isVisible = (id: string) => columnVisibility[id] !== false;
  const setVisibility = (id: string, next: boolean) => setColumnVisibility((prev) => ({ ...prev, [id]: next }));
  const toggleAll = (next: boolean) =>
    setColumnVisibility(Object.fromEntries(CATEGORY_COLUMNS.map((c) => [c.id, next])));

  const hasFilter = typeId !== null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-dewey-title capitalize">{t("model.category.modelNames")}</h1>
          <div className="mt-1 text-14-regular text-fg-3 tabular-nums">
            {t("feature.categories.subtitle", { count: filteredRows.length })}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-3" />
            <Input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={t("ui.search")}
              className="h-9 w-[240px] pl-8"
              aria-label={t("ui.search")}
            />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label={t("ui.filter")}
                className={cn(hasFilter && "border-primary-500 text-primary-700")}
              >
                <Filter className={cn(hasFilter && "fill-primary-100")} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[200px]">
              <DropdownMenuLabel>{t("model.category.type")}</DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={typeId ?? ALL}
                onValueChange={(v) => onTypeIdChange(v === ALL ? null : (v as TypeFilter))}
              >
                <DropdownMenuRadioItem value={ALL}>{t("ui.all")}</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="entity">{t("model.record.entity.modelName")}</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="relation">{t("model.record.relation.modelName")}</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" size="icon" aria-label={t("ui.columns")}>
                <Columns3 />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[200px]">
              <DropdownMenuItem onSelect={() => toggleAll(false)}>{t("ui.hideAll")}</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => toggleAll(true)}>{t("ui.showAll")}</DropdownMenuItem>
              <DropdownMenuSeparator />
              {CATEGORY_COLUMNS.map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.id}
                  checked={isVisible(col.id)}
                  onCheckedChange={(checked) => setVisibility(col.id, checked === true)}
                  onSelect={(e) => e.preventDefault()}
                >
                  {t(col.labelKey)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button type="button" variant="outline" size="icon" onClick={onRefresh} aria-label={t("ui.reload")}>
            <RotateCw />
          </Button>
          <label
            htmlFor="categories-exclude-deactivated"
            className="inline-flex items-center gap-2 text-14-regular text-fg-2"
          >
            <Checkbox
              id="categories-exclude-deactivated"
              checked={excludeDeactivated}
              onCheckedChange={(v) => onExcludeDeactivatedChange(v === true)}
            />
            <span>{t("ui.excludeDeactivated")}</span>
          </label>
          {isActiveRolloAdmin && (
            <Button type="button" size="sm" onClick={() => setCreateOpen(true)}>
              <Plus />
              <span>{t("ui.add")}</span>
            </Button>
          )}
        </div>
      </div>

      <CategoryFormDialog open={createOpen} onOpenChange={setCreateOpen} />
      <CategoriesTable
        rows={filteredRows}
        onSelect={onSelect}
        emptyMessage={t("feature.categories.empty")}
        globalFilter={search}
        onGlobalFilterChange={onSearchChange}
        columnFilters={columnFilters}
        pageIndex={pageIndex}
        onPageIndexChange={onPageIndexChange}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
      />
    </div>
  );
}
